import SponAirdropAbi from "@/contracts/abis/SPONAirdrop.json";
import SponVestingAbi from "@/contracts/abis/SPONVesting.json";
import {
  sponAirdropContract,
  stSponVestingContract,
} from "@/contracts/addresses";
import { withFallbackProvider } from "@/utils/rpc-provider";
import { ethers } from "ethers";

export interface ClaimWindow {
  /** Unix timestamp (seconds) after which the contract rejects claims. */
  deadline: number | null;
  isClosed: boolean;
}

export interface ClaimWindows {
  airdrop: ClaimWindow;
  vesting: ClaimWindow;
}

export const OPEN_CLAIM_WINDOW: ClaimWindow = { deadline: null, isClosed: false };

/**
 * Neither contract exposes a setter for `claimDeadline`, so once it passes the
 * claim is permanently closed on-chain and `claimTokens` / `claimAirdrop` revert
 * with `ClaimPeriodHasEnded` / `AirdropClaimPeriodEnded`. Read it up front so we
 * can tell the user before they burn gas on a doomed transaction.
 */
const readClaimDeadline = async (
  address: string,
  abi: ethers.InterfaceAbi,
  provider: ethers.Provider
): Promise<ClaimWindow> => {
  const contract = new ethers.Contract(address, abi, provider);
  const deadline = Number(await contract.claimDeadline());

  // A zero deadline means "not configured" - never lock the UI on that.
  if (!Number.isFinite(deadline) || deadline <= 0) return OPEN_CLAIM_WINDOW;

  return {
    deadline,
    isClosed: Date.now() >= deadline * 1000,
  };
};

/**
 * Reads both claim deadlines. Fails open: if the RPC is unreachable we let the
 * user attempt the claim rather than block it on a network hiccup.
 */
export const getClaimWindows = async (): Promise<ClaimWindows> => {
  try {
    return await withFallbackProvider(async (provider) => {
      const [airdrop, vesting] = await Promise.all([
        readClaimDeadline(
          sponAirdropContract,
          SponAirdropAbi as ethers.InterfaceAbi,
          provider
        ),
        readClaimDeadline(
          stSponVestingContract,
          SponVestingAbi as ethers.InterfaceAbi,
          provider
        ),
      ]);
      return { airdrop, vesting };
    });
  } catch (error) {
    console.error("Error fetching claim deadlines:", error);
    return { airdrop: OPEN_CLAIM_WINDOW, vesting: OPEN_CLAIM_WINDOW };
  }
};

export const formatClaimDeadline = (deadline: number | null): string | null =>
  deadline
    ? new Date(deadline * 1000).toLocaleDateString("en-GB", {
        day: "numeric",
        month: "long",
        year: "numeric",
      })
    : null;
